// _dualWriteHelpers.js
//
// Återanvändbart dual-write-mönster för frontend services som migrerar till
// backend. Wrappa en service-klass och få:
//   - dualWriteJson(baseKey, value)        async — local + fire-and-forget backend
//   - dualWriteFlag(baseKey, value)        async — boolean flags
//   - dualWriteJsonSync(baseKey, value)    sync — för seed-paths
//   - dualWriteFlagSync(baseKey, value)
//   - syncStateToBackend(baseKey, value)   internal
//   - initFromBackend(localKeys, flagKeys) async — pull + merge vid login
//
// Konventioner:
//   service._entityKey(baseKey)  — måste finnas på service:n; returnerar
//                                   localStorage-nyckel (vanligen "<base>_<entityId>").
//   service.notifyListeners()    — anropas efter merge i initFromBackend.
//   storage                      — passas in (samma StorageAdapter-instans).
//   api                          — { putState, getAllState, patchState }
//                                   från createServiceStateApi.

export function makeDualWriteMixin({ storage, api, serviceName = 'service' }) {
  // ADM-213: tidigare warnedOnce-flagga tystade alla efterföljande fel.
  // Det dolde Pi-rapporterade ConfigTemplates-bugg där backend-anropet
  // failade men UI fick "save success"-toast. Nu loggas första felet per
  // unik (op, key, status)-kombination med tydlig markör.
  const warned = new Set();
  const warn = (op, key, err) => {
    const status = err?.status || err?.response?.status || 'unknown';
    const sig = `${op}:${key}:${status}`;
    if (warned.has(sig)) return;
    warned.add(sig);
    console.warn(
      `[${serviceName}] backend-sync FAILED — op=${op} key=${key} status=${status}`,
      err?.message || err
    );
    // Expose till window.__rmsDebug så Pi kan inspektera utan console-access
    if (typeof window !== 'undefined') {
      const debug = window.__rmsDebug || {};
      const failures = debug.dualWriteFailures || [];
      failures.push({
        service: serviceName,
        op, key, status,
        message: err?.message || String(err),
        at: new Date().toISOString(),
      });
      window.__rmsDebug = { ...debug, dualWriteFailures: failures.slice(-50) };
    }
  };

  return {
    _syncStateToBackend(baseKey, value) {
      try {
        api.putState(baseKey, value).catch((err) => warn('put', baseKey, err));
      } catch (err) {
        warn('put-throw', baseKey, err);
      }
    },

    async _dualWriteJson(baseKey, value) {
      await storage.setJSON(this._entityKey(baseKey), value);
      this._syncStateToBackend(baseKey, value);
    },

    async _dualWriteFlag(baseKey, value) {
      await storage.setItem(this._entityKey(baseKey), String(value));
      this._syncStateToBackend(baseKey, value === true || value === 'true');
    },

    _dualWriteJsonSync(baseKey, value) {
      storage.setJSON(this._entityKey(baseKey), value);
      this._syncStateToBackend(baseKey, value);
    },

    _dualWriteFlagSync(baseKey, value) {
      storage.setItem(this._entityKey(baseKey), String(value));
      this._syncStateToBackend(baseKey, value === true || value === 'true');
    },

    /**
     * Pull alla state-keys från backend och merger in localStorage.
     * Backend vinner vid konflikt. Om backend tom — push lokala värden upp.
     *
     * @param {string[]} jsonKeys — base-keys för JSON-värden
     * @param {string[]} flagKeys — base-keys för boolean-flags
     */
    async _initFromBackend(jsonKeys = [], flagKeys = []) {
      try {
        const remote = await api.getAllState();
        if (!remote || Object.keys(remote).length === 0) {
          // Backend tom — push lokala värden för aktiv entity
          const upload = {};
          for (const k of jsonKeys) {
            const v = storage.getJSON(this._entityKey(k), null);
            if (v !== null) upload[k] = v;
          }
          for (const k of flagKeys) {
            const v = storage.getItem(this._entityKey(k));
            if (v !== null) upload[k] = v === 'true';
          }
          if (Object.keys(upload).length > 0) {
            await api.patchState(upload);
          }
          return;
        }

        // Merge: backend vinner per nyckel
        for (const [key, value] of Object.entries(remote)) {
          if (flagKeys.includes(key)) {
            storage.setItem(this._entityKey(key), String(value));
          } else {
            storage.setJSON(this._entityKey(key), value);
          }
        }
        if (typeof this.notifyListeners === 'function') this.notifyListeners();
      } catch (err) {
        console.debug(`[${serviceName}] initFromBackend skipped:`, err?.message);
      }
    },
  };
}

/** Applicera mixin-objekt på en klass-prototyp. */
export function applyDualWriteMixin(targetClass, mixin) {
  Object.assign(targetClass.prototype, mixin);
}
